// BarChart.js
import moment from 'moment';
import * as d3 from 'd3';
import React, { useState, useEffect, useReducer, useContext, useRef } from 'react';
import { Spinner, HTMLSelect, Drawer, H1, H6, H5 } from '@blueprintjs/core';

import { itemReducer } from './itemReducers';
import { ItemContext } from '../App';
import {
  PromaDesignBlocks,
  PromaBlocks,
  PromaItemType,
  RiskItemType,
  TeknAlat
} from './itemDefinitions';
import GeneralMultiSelect, { enumToArray } from '../views/GeneralMultiSelect';
import { itemDueClass } from './MapItems';
import { dateFormat } from '../common/dateFormat';

const formatValue = d3
  .formatLocale({
    decimal: ',',
    thousands: '\u00a0',
    grouping: [3],
    currency: ['', '\u00a0€']
  })
  .format('$.3~s');

const formatVal2 = d3.format('.2f');

const formatTime = d3.timeFormat('%B %d, %Y');

export const promaBlockToDesign = (promaBlock) => {
  if (promaBlock.includes('5')) {
    return PromaDesignBlocks.L5;
  }
  if (promaBlock === PromaBlocks.L1) {
    return PromaDesignBlocks.L1;
  }
  if (promaBlock === PromaBlocks.L2A) {
    return PromaDesignBlocks.L2A;
  }
  if (promaBlock === PromaBlocks.L2B) {
    return PromaDesignBlocks.L2B;
  }
  if (promaBlock === PromaBlocks.L3A) {
    return PromaDesignBlocks.L3A;
  }
  if (promaBlock === PromaBlocks.L3B) {
    return PromaDesignBlocks.L3B;
  }
  if (promaBlock === PromaBlocks.L3_YHT) {
    return PromaDesignBlocks.L3_YHT;
  }
  if (promaBlock === PromaBlocks.L4) {
    return PromaDesignBlocks.L4;
  }
  if (promaBlock === PromaBlocks.SÖR_T) {
    return PromaDesignBlocks.SOR_T;
  }
  return PromaDesignBlocks.LAPI;
};

export const designBlockFromVal = (block) => {
  if (block.includes('5')) {
    return PromaDesignBlocks.L5;
  }
  if (block.includes('1')) {
    return PromaDesignBlocks.L1;
  }
  if (block.includes('2A')) {
    return PromaDesignBlocks.L2A;
  }
  if (block.includes('2B')) {
    return PromaDesignBlocks.L2B;
  }
  if (block.includes('3A')) {
    return PromaDesignBlocks.L3A;
  }
  if (block.includes('3B')) {
    return PromaDesignBlocks.L3B;
  }
  if (block.includes('3_YHT')) {
    return PromaDesignBlocks.L3_YHT;
  }
  if (block.includes('4')) {
    return PromaDesignBlocks.L4;
  }
  if (block.includes('SOR_T')) {
    return PromaDesignBlocks.SOR_T;
  }
  return PromaDesignBlocks.LAPI;
};

const DesignStatusChart = ({
  height,
  width,
  margin,
  dAchievements,
  dSItems,
  inHeight,
  inWidth,
  filterBlocks,
  filterTeknAla,
  splitElements,
  hankeJako,
  total,
  preReady,
  readyCount,
  setOverLayOpen
}) => {
  // const filteredData = data.filter((d) => d.block === filterDict.currentBlock);
  const ref = useRef();

  let rangeEnd;
  if (splitElements === 'combine') {
    rangeEnd = new Date('2022-03-31');
  } else {
    rangeEnd = new Date('2022-03-31');
  }

  const timeRange = [new Date('2021-04-01'), rangeEnd];

  const invertTime = (time) => d3.scaleTime().domain(timeRange).range([0, 1000]).invert(time);
  const timeArray = Array.from({ length: 1000 }, (v, k) => k + 1).map((val) => invertTime(val));

  let dataArrays;
  let texts;
  let colors;
  if (splitElements === 'combine') {
    dataArrays = [
      timeArray.map((time) => ({
        perc:
          ((dAchievements.filter((item) => moment(item.endDate).isBefore(time)).length + preReady) /
            total) *
          100,
        time
      })),
      timeArray
        .filter((time) => moment(time).isBefore(new Date()))
        .map((time) => ({
          perc:
            ((dAchievements.filter((item) => moment(item.finishedDate).isBefore(time)).length +
              preReady) /
              total) *
            100,
          time
        }))
    ];
    texts = ['Tavoite', 'Toteuma'];
    colors = ['#738694', '#137CBD'];
  } else if (splitElements === 'tekn') {
    const preReadies = filterTeknAla.map((tekn) =>
      dSItems
        .filter((dSItem) => dSItem.TeknAla === tekn.id)
        .reduce((sum, item) => {
          return sum + item['Valmiit ennen PROMAa'];
        }, 0)
    );
    const totals = filterTeknAla.map((tekn) =>
      dSItems
        .filter((dSItem) => dSItem.TeknAla === tekn.id)
        .reduce((sum, item) => {
          return sum + item['Kokonaismäärä arvio'];
        }, 0)
    );
    dataArrays = filterTeknAla.map((teknAla, i) =>
      timeArray
        .filter((time) => moment(time).isBefore(new Date()))
        .map((time) => ({
          perc:
            ((dAchievements
              .filter(
                (item) =>
                  teknAla.name === item.techniqueArea ||
                  (item.techniqueArea === 'Liikenne' &&
                    teknAla.name === 'Työnaikaiset liikennejärjestelyt')
              )
              .filter((item) => moment(item.finishedDate).isBefore(time)).length +
              preReadies[i]) /
              totals[i]) *
            100,
          time
        }))
    );
    texts = filterTeknAla.map((teknAla) => teknAla.name);
    colors = [
      '#D1E1FF',
      '#C1C7F8',
      '#AFAEEF',
      '#9B95E5',
      '#857FD9',
      '#6C6ACA',
      '#4E58B6',
      '#1F4B99'
    ];
  }

  let x;
  let y;

  const draw = () => {
    d3.select(ref.current).selectAll('*').remove();

    const svg = d3
      .select(ref.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    // Add X axis
    const x = d3.scaleTime().domain(timeRange).range([0, inWidth]).nice();

    const g = svg.append('g').style('font', '16px arial').style('font-weight', 'bold');
    g.attr('transform', 'translate(0,' + inHeight + ')').call(
      d3.axisBottom(x) // .tickSize(-inHeight * 0.7)
      // .tickValues([1900, 1925, 1975, 2000])
    );
    // .select('.domain')
    // .remove();
    // Customization
    svg.selectAll('.tick line').attr('stroke', '#b8b8b8');

    // const g2 = svg.append('g').style('font', '16px arial').style('font-weight', 'bold');
    // g.attr('transform', 'translate(0,' + inHeight + ')').call(d3.axisBottom(x).ticks(8, '~s'));

    // Add Y axis
    y = d3.scaleLinear().domain([0, 100]).range([inHeight, 0]);

    const g2 = svg.append('g').style('font', '16px arial').style('font-weight', 'bold');
    g2.attr('transform', 'translate(0,0)').call(d3.axisLeft(y).tickFormat((y) => `${y}%`));

    // create a tooltip
    const Tooltip = svg
      .append('text')
      .attr('x', 0)
      .attr('y', 0)
      .style('opacity', 0)
      .style('font-size', 17);

    // What happens when the mouse move -> show the annotations at the right positions.
    function mouseover() {
      focusList.map((focus) => focus.style('opacity', 1));
      focusTextList.map((focus) => focus.style('opacity', 1));
    }

    const format = formatValue;

    function mousemove(event) {
      // recover coordinate we need
      const x0 = x.invert(d3.pointer(event)[0]);
      for (const [i, dataArray] of dataArrays.entries()) {
        const a = bisect(dataArray, x0);
        const selectedData = dataArray[a];
        if (selectedData !== undefined) {
          focusList[i].attr('cx', x(selectedData.time)).attr('cy', y(selectedData.perc));
          focusTextList[i]
            .html(
              texts[i] +
                ' ' +
                formatVal2(selectedData.perc) +
                '%' +
                ' - ' +
                formatTime(selectedData.time)
            )
            .attr('x', x(selectedData.time) + 15)
            .attr('y', y(selectedData.perc));
        }
      }
    }

    function mouseout() {
      focusList.map((focus) => focus.style('opacity', 0));
      focusTextList.map((focus) => focus.style('opacity', 0));
    }

    const openLink = (d, i) => {
      console.log(i);
    };

    // This allows to find the closest X index of the mouse:
    const bisect = d3.bisector(function (d) {
      return d.time;
    }).left;

    // TODO move before rect that collects mouse location
    const lines = dataArrays.map((array, i) =>
      svg
        .append('g')
        .append('path')
        .datum(array)
        .attr(
          'd',
          d3
            .line()
            .curve(d3.curveBasis)
            .x(function (d) {
              return x(d.time);
            })
            .y(function (d) {
              return y(d.perc);
            })
        )
        .attr('fill', 'none')
        .attr('stroke', colors[i])
        .attr('stroke-width', 4)
    );
    // Create the circle that travels along the curve of chart
    const focusList = dataArrays.map((array) =>
      svg
        .append('g')
        .append('circle')
        .style('fill', 'none')
        .attr('stroke', 'black')
        .attr('r', 8.5)
        .style('opacity', 0)
    );

    // Create the text that travels along the curve of chart
    const focusTextList = focusList.map((array) =>
      svg
        .append('g')
        .append('text')
        .style('opacity', 0)
        .attr('text-anchor', 'left')
        .attr('alignment-baseline', 'middle')
    );

    // Create a rect on top of the svg area: this rectangle recovers mouse position
    svg
      .append('rect')
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .attr('width', width)
      .attr('height', height)
      .on('mouseover', mouseover)
      .on('mousemove', mousemove)
      .on('mouseout', mouseout);

    // Add X axis label:
    svg
      .append('text')
      .attr('text-anchor', 'end')
      .attr('x', inWidth)
      .attr('y', inHeight - 30)
      .text(`${preReady + readyCount}/${total} tuotosta valmiina`)
      .on('click', () => setOverLayOpen(true))
      .style('cursor', 'pointer');
  };

  // TODO doesnt work atm
  // useEffect(() => {
  //   const newData = data.filter((d) => d.block === filterDict.currentBlock);
  //   if (line !== undefined) {
  //     line
  //       .datum(newData)
  //       .transition()
  //       .duration(100)
  //       .attr(
  //         'd',
  //         d3
  //           .line()
  //           .curve(d3.curveBasis)
  //           .x(function (d) {
  //             return x(d.value);
  //           })
  //           .y(function (d) {
  //             return y(d.perc);
  //           })
  //       )
  //       .attr('fill', 'none')
  //       .attr('stroke', '#5C7080')
  //       .attr('stroke-width', 4);
  //   }
  // }, [filterDict.currentBlock]);

  useEffect(() => {
    draw();
  }, [height, width, dAchievements, dSItems, inHeight, inWidth]);

  return (
    <div className="chart">
      <svg ref={ref} />
    </div>
  );
};

const DesignStatusItems = () => {
  const [height, setHeight] = useState(window.innerHeight - 160);
  const [width, setWidth] = useState(window.innerWidth);
  const [filterBlocks, dispatchFilterBlocks] = useReducer(itemReducer, []);
  const [filterTeknAla, dispatchFilterTeknAla] = useReducer(itemReducer, []);
  const [splitElements, setSplitElements] = useState('combine');
  const [hankeJako, setHankeJako] = useState('koko_hanke');
  const [overLayIsOpen, setOverLayOpen] = useState(false);

  const { items } = useContext(ItemContext);

  const dSItems = items.filter((item) => item.itemType === PromaItemType.DesignInfo);
  const filteredDSItems = dSItems
    .filter(
      (item) =>
        hankeJako === 'koko_hanke' ||
        (hankeJako === 'perus' && item.Lohko.includes('PERUS')) ||
        (hankeJako === 'liha' && item.Lohko.includes('LIHA'))
    )
    .filter((item) => {
      return (
        filterBlocks.length === 0 ||
        filterBlocks.map((block) => block.name).includes(designBlockFromVal(item.Lohko))
      );
    })
    .filter(
      (item) =>
        filterTeknAla.length === 0 || filterTeknAla.map((tekn) => tekn.id).includes(item.TeknAla)
    );

  const dAchievements = items.filter(
    (item) =>
      item.itemType === PromaItemType.Achievement && item.milestoneType === 'planning-milestone'
  );
  const filteredDAchievements = dAchievements
    .filter(
      (item) =>
        hankeJako === 'koko_hanke' ||
        (hankeJako === 'perus' && item.programDivision.includes('Perushanke')) ||
        (hankeJako === 'liha' && item.programDivision.includes('Liittyvä hanke'))
    )
    .filter((item) => {
      return (
        filterBlocks.length === 0 ||
        filterBlocks.map((block) => block.name).includes(promaBlockToDesign(item.block))
      );
    })
    .filter((item) => {
      return (
        filterTeknAla.length === 0 ||
        filterTeknAla.map((tekn) => tekn.name).includes(item.techniqueArea) ||
        (item.techniqueArea === 'Liikenne' &&
          filterTeknAla.map((tekn) => tekn.name).includes('Työnaikaiset liikennejärjestelyt'))
      );
    });

  const margin = { top: 50, right: 30, bottom: 40, left: 50 };
  const inWidth = width - margin.left - margin.right;
  const inHeight = height - margin.top - margin.bottom;

  /** Update the size of the graph element - with a short delay to avoid too many renders */
  const updateGraphSize = () => {
    setTimeout(() => {
      if (height !== window.innerHeight - 100) {
        setHeight(window.innerHeight - 100);
      }
      if (width !== window.innerWidth) {
        setWidth(window.innerWidth);
      }
    }, 200);
  };

  window.addEventListener('resize', updateGraphSize);

  const filtrableBlocks = enumToArray(PromaDesignBlocks, PromaItemType.Block);
  const filtrableTeknAla = enumToArray(TeknAlat, PromaItemType.TeknAla);

  const total = filteredDSItems.reduce((sum, item) => {
    return sum + item['Kokonaismäärä arvio'];
  }, 0);
  const preReady = filteredDSItems.reduce((sum, item) => {
    return sum + item['Valmiit ennen PROMAa'];
  }, 0);
  const readyAchievements = filteredDAchievements.filter((item) => item.status === 'Valmis');

  const chartProps = {
    height,
    width,
    margin,
    inHeight,
    inWidth,
    dSItems: filteredDSItems,
    dAchievements: filteredDAchievements,
    filterBlocks: filterBlocks.length > 0 ? filterBlocks : filtrableBlocks,
    filterTeknAla: filterTeknAla.length > 0 ? filterTeknAla : filtrableTeknAla,
    splitElements,
    hankeJako,
    total,
    preReady,
    readyCount: readyAchievements.length,
    setOverLayOpen
  };

  if (dSItems.length === 0 || dAchievements.length === 0) {
    return (
      <div>
        <Spinner className="centered" size={100} />
      </div>
    );
  }

  return (
    <div>
      <Drawer isOpen={overLayIsOpen} onClose={() => setOverLayOpen(false)} title="Tuotokset">
        <div style={{ overflow: 'auto' }}>
          <H5>
            Yhteensä {total} tuotosta, joista {preReady} valmistui ennen PROMAn käyttöönottoa ja
            arviolta {total - readyAchievements.length - preReady} tuotosta yhä kirjaamatta PROMAan.
          </H5>
          <H5>Valmiit tuotokset:</H5>{' '}
          <ul>
            {readyAchievements
              .sort((a, b) =>
                moment(a.finishedDate, dateFormat).diff(moment(b.finishedDate, dateFormat), 'days')
              )
              .map((item) => (
                <li key={item.id} className={itemDueClass(item)}>
                  {item.finishedDate} {item.name} ({item.block})
                </li>
              ))}
          </ul>{' '}
          <H5>Keskeneräiset tuotokset:</H5>{' '}
          <ul>
            {filteredDAchievements
              .filter((item) => item.status !== 'Valmis')
              .sort((a, b) =>
                moment(a.endDate, dateFormat).diff(moment(b.endDate, dateFormat), 'days')
              )
              .map((item) => (
                <li key={item.id} className={itemDueClass(item)}>
                  {item.endDate} {item.name} ({item.block})
                </li>
              ))}
          </ul>{' '}
        </div>
      </Drawer>
      <GeneralMultiSelect
        key="Lohko"
        placeholderText="Lohko"
        filtrableItems={filtrableBlocks}
        filterItems={filterBlocks}
        dispatchFilterItems={dispatchFilterBlocks}
      />
      <GeneralMultiSelect
        key="TeknAla"
        placeholderText="Tekn. alat"
        filtrableItems={filtrableTeknAla}
        filterItems={filterTeknAla}
        dispatchFilterItems={dispatchFilterTeknAla}
      />
      <HTMLSelect
        value={hankeJako}
        onChange={(e) => setHankeJako(e.currentTarget.value)}
        options={[
          { label: 'Koko hanke', value: 'koko_hanke' },
          { label: 'Perushanke', value: 'perus' },
          { label: 'Liittyvät hankkeet', value: 'liha' }
        ]}
      />
      <HTMLSelect
        value={splitElements}
        onChange={(e) => setSplitElements(e.currentTarget.value)}
        options={[
          { label: 'Kaikki yhdessä', value: 'combine' },
          { label: 'Tekniikka-aloittain', value: 'tekn' }
          // { label: 'Lohkoittain', value: 'block' }
        ]}
      />
      <DesignStatusChart {...chartProps} />
      <div>
        Seuranta perustuu täydennettyihin arvioihin{' '}
        <a
          href="https://proma-front-dot-kalasatama-294516.ey.r.appspot.com/"
          target="_blank"
          rel="noopener noreferrer"
        >
          PROMA
        </a>
        n suunnittelun välitavoitteiden tuotoksista{' '}
        <a
          href="https://docs.google.com/spreadsheets/d/1DMYDqijA5RJ7RtukbBSX-vNX7sklM_1DQQXV5FF6-PA/"
          target="_blank"
          rel="noopener noreferrer"
        >
          täällä
        </a>
        .
      </div>
    </div>
  );
};

export default DesignStatusItems;
